import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { fetchSchoolSubjectChangeRequests, reviewAdminChangeRequest } from "../../../utils/termSubjectApi";
import { getSchoolIdForUser } from "../../../utils/userContext";

export default function AdminSubjectChangeRequests() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const adminId = currentUser?._id || currentUser?.id;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const data = await fetchSchoolSubjectChangeRequests(schoolId, "PendingAdmin");
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load requests.");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleReview = async (id, approve) => {
        try {
            await reviewAdminChangeRequest(id, { adminId, approve });
            setMessage(approve ? "Change applied for the current term." : "Request rejected.");
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not process request.");
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <SwapHorizOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Subject change approvals
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Final approval for mid-term subject changes (after form teacher review). No fee impact.
                    </Typography>
                </Box>
            </Stack>

            {message ? (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            ) : null}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Teacher</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Term</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                                            No requests awaiting director approval.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((r) => (
                                    <TableRow key={r._id || r.id}>
                                        <TableCell>{r.requester?.name}</TableCell>
                                        <TableCell>{r.sclass?.sclassName}</TableCell>
                                        <TableCell>{r.subject?.subName}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={[r.term, r.academicYear].filter(Boolean).join(" · ")} variant="outlined" />
                                        </TableCell>
                                        <TableCell>{r.reason || "—"}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" variant="contained" onClick={() => handleReview(r._id || r.id, true)}>
                                                Approve
                                            </Button>
                                            <Button size="small" color="error" onClick={() => handleReview(r._id || r.id, false)}>
                                                Reject
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Container>
    );
}
